// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import * as VehicleGroupAction from '../actions/vehicle-group.actions';
import {
  NotificationService,
  VehicleGroup,
  VehicleTypeApiService,
} from '@fc-core';
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { select, Store } from '@ngrx/store';
import * as fromVehicles from '../reducers';
import { getDefaultColors } from '../selectors';
import { vehicleGroupsError } from '../actions';
import { EMPTY, of } from 'rxjs';
import { Router } from '@angular/router';
import { getErrorMessage } from '@fc-core/services/notification.utils';

@Injectable()
export class VehicleGroupEffects {
  getGroups$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VehicleGroupAction.loadVehicleGroups),
      switchMap(() => {
        return this.vehicleTypeApi.loadAll().pipe(
          map((vehicleGroups: VehicleGroup[]) => {
            return VehicleGroupAction.loadVehicleGroupsSuccess({
              vehicleGroups,
            });
          }),
          catchError((error) => of(vehicleGroupsError({ error }))),
        );
      }),
    ),
  );

  createVehicleGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VehicleGroupAction.addVehicleGroup),
      switchMap(({ vehicleGroup }) => {
        return this.vehicleTypeApi.create(vehicleGroup).pipe(
          map((group: VehicleGroup) => {
            this.notifications.showSuccess('Group successfully created');
            this.dialog.closeAll();
            return VehicleGroupAction.addVehicleGroupSuccess({
              vehicleGroup: group,
            });
          }),
          catchError((error) => of(vehicleGroupsError({ error }))),
        );
      }),
    ),
  );

  getVehicleGroupDefaultColors$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VehicleGroupAction.getVehicleGroupDefaultColors),
      withLatestFrom(this.store.pipe(select(getDefaultColors))),
      switchMap(() => {
        return this.vehicleTypeApi.getDefaultColor().pipe(
          map((colors) => {
            return VehicleGroupAction.getVehicleGroupDefaultColorsSuccess({
              colors,
            });
          }),
          catchError((error) => of(vehicleGroupsError({ error }))),
        );
      }),
    ),
  );

  updateVehicleGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VehicleGroupAction.updateVehicleGroup),
      switchMap(({ vehicleGroup }) => {
        return this.vehicleTypeApi.update(vehicleGroup).pipe(
          map((group: VehicleGroup) => {
            this.notifications.showSuccess('Group successfully updated');
            this.dialog.closeAll();
            return VehicleGroupAction.updateVehicleGroupSuccess({
              vehicleGroup: { id: group.id, changes: group },
            });
          }),
          catchError((error) => of(vehicleGroupsError({ error }))),
        );
      }),
    ),
  );

  deleteVehicleGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VehicleGroupAction.deleteVehicleGroup),
      switchMap(({ id }) => {
        return this.vehicleTypeApi.delete(id).pipe(
          map(() => {
            this.notifications.showSuccess('Group successfully deleted');
            this.router.navigate(['/vehicles/main/vehicle-groups/', '']);
            this.dialog.closeAll();
            return VehicleGroupAction.deleteVehicleGroupSuccess({ id });
          }),
          catchError((error) => of(vehicleGroupsError({ error }))),
        );
      }),
    ),
  );
  removeVehicleFromVehicleGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VehicleGroupAction.removeVehicleFromGroup),
      switchMap(({ groupId, vehicleId }) => {
        return this.vehicleTypeApi
          .removeVehicleFromGroup(groupId, vehicleId)
          .pipe(
            map((vehicleGroup: VehicleGroup) => {
              this.notifications.showSuccess(
                'Vehicle successfully removed from group',
              );
              return VehicleGroupAction.removeVehicleFromGroupSuccess({
                vehicleGroup,
                vehicleId,
              });
            }),
            catchError((error) => of(vehicleGroupsError({ error }))),
          );
      }),
    ),
  );

  addVehicleToVehicleGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VehicleGroupAction.addVehicleToGroup),
      switchMap(({ groupId, vehicleId }) => {
        return this.vehicleTypeApi
          .addVehiclesToGroup(groupId, [vehicleId])
          .pipe(
            map((vehicleGroup: VehicleGroup) => {
              this.notifications.showSuccess(
                'Vehicle successfully added to group',
              );
              return VehicleGroupAction.addVehicleToGroupSuccess({
                vehicleGroup,
              });
            }),
            catchError((error) => of(vehicleGroupsError({ error }))),
          );
      }),
    ),
  );

  addVehiclesToVehicleGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VehicleGroupAction.addVehiclesToGroup),
      switchMap(({ groupId, vehicleIdList }) => {
        return this.vehicleTypeApi
          .addVehiclesToGroup(groupId, vehicleIdList)
          .pipe(
            map((vehicleGroup: VehicleGroup) => {
              this.notifications.showSuccess(
                'Vehicles successfully added to group',
              );
              this.dialog.closeAll();
              return VehicleGroupAction.addVehiclesToGroupSuccess({
                vehicleGroup,
                vehicleIdList,
              });
            }),
            catchError((error) => of(vehicleGroupsError({ error }))),
          );
      }),
    ),
  );

  vehicleGroupsError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(VehicleGroupAction.vehicleGroupsError),
        switchMap(({ error }) => {
          this.notifications.showError(getErrorMessage(error));
          return EMPTY;
        }),
      ),
    { dispatch: false },
  );

  constructor(
    private router: Router,
    private actions$: Actions,
    private vehicleTypeApi: VehicleTypeApiService,
    private dialog: MatDialog,
    private store: Store<fromVehicles.State>,
    private notifications: NotificationService,
  ) {}
}
